// Squarified treemap. SVG rects stretch to fill the container; HTML overlay
// labels are positioned in real pixel space so they don't deform with the
// container aspect ratio. Lifted from view-terminal.jsx and typed.

import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { COLORS, FONT_CONDENSED, FONT_MONO, fmtRaw, tStyles } from "../styles";

export interface TreemapItem {
  name: string;
  value: number;
  velocity: number | null;
  raw: number; // shown beneath name
  spark7d?: number[]; // 7 daily-aggregated points, normalised 0..1
  watched?: boolean;
}

interface PlacedItem extends TreemapItem {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  items: TreemapItem[];
  selectedName: string | null;
  cursorName: string | null;
  fontScale: number;
  // sum of `raw` across all items in the current window. When > 0, large
  // tiles get a corner share-of-voice label (raw / total). 0 = hide.
  totalRaw?: number;
  // External hover (e.g., from the stacked-bars band). Matching tile gets
  // an accent stroke + full opacity; non-matching tiles dim. null = off.
  highlightName?: string | null;
  // When true, fade ALL tiles uniformly so the chart's overlay lines (in
  // StackedBars) get the eye's attention. Hover/highlight still over-rides
  // the dim for the matched tile.
  dimAll?: boolean;
  onHoverChange?: (name: string | null) => void;
  // Emitted whenever the set of folded-into-Others companies changes.
  // The stacked-bars band uses this so its grouping matches what's
  // visible here (one tile = one segment, one Others = one Others).
  onFoldedNamesChange?: (names: string[]) => void;
  onSelect: (name: string) => void;
}

export function Treemap({
  items,
  selectedName,
  cursorName,
  fontScale,
  totalRaw = 0,
  highlightName = null,
  dimAll = false,
  onHoverChange,
  onFoldedNamesChange,
  onSelect,
}: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 880, h: 540 });

  useEffect(() => {
    if (!boxRef.current) return;
    const el = boxRef.current;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r.width > 10 && r.height > 10) setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const W = 880;
  const H = 540;
  const sx = box.w / W;
  const sy = box.h / H;

  const { placed, foldedNames } = useMemo(
    () => squarifyWithFolding(items, W, H, sx, sy),
    [items, sx, sy],
  );

  // Push folded set up so the stacked-bars band can mirror it. Effect
  // (not call-during-render) so we don't trigger a parent setState while
  // rendering. Comparison key avoids firing for cosmetic re-renders that
  // happen to produce a new array with identical contents.
  const foldedKey = foldedNames.join(",");
  useEffect(() => {
    onFoldedNamesChange?.(foldedNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldedKey, onFoldedNamesChange]);

  // Folded names map back to the synthetic "Others" tile. Resolve external
  // names (cursor/selected/highlight) so they keep working when their
  // company gets folded.
  const placedNameSet = useMemo(
    () => new Set(placed.map((p) => p.name)),
    [placed],
  );
  const foldedSet = useMemo(() => new Set(foldedNames), [foldedNames]);
  const resolveTile = (name: string | null): string | null => {
    if (!name) return null;
    if (placedNameSet.has(name)) return name;
    if (foldedSet.has(name)) return "Others";
    return null;
  };
  const cursorTile = resolveTile(cursorName);
  const selectedTile = resolveTile(selectedName);
  const highlightTile = resolveTile(highlightName);
  const dimMode = highlightTile !== null || dimAll;

  return (
    <div style={tStyles.treemapBox} ref={boxRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      >
        {placed.map((t) => {
          const isOthers = t.name === "Others";
          const isSel = selectedTile === t.name;
          const isCursor = cursorTile === t.name;
          const isHL = highlightTile === t.name;
          const dim = dimMode && !isHL;
          return (
            <rect
              key={t.name}
              x={t.x}
              y={t.y}
              width={t.w}
              height={t.h}
              onClick={() => onSelect(t.name)}
              onMouseEnter={
                onHoverChange ? () => onHoverChange(t.name) : undefined
              }
              onMouseLeave={
                onHoverChange ? () => onHoverChange(null) : undefined
              }
              style={{ cursor: isOthers ? "default" : "pointer" }}
              fill={heatColor(t.velocity)}
              fillOpacity={dim ? 0.35 : 1}
              stroke={
                isSel
                  ? COLORS.accent
                  : isHL
                    ? COLORS.accent
                    : isCursor
                      ? "#7a8a82"
                      : isOthers
                        ? COLORS.borderHi
                        : COLORS.bg
              }
              strokeWidth={isSel ? 2 : isHL ? 2 : isCursor ? 1.5 : 1}
              strokeDasharray={isOthers && !isSel && !isHL ? "4 3" : undefined}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>
      {placed.map((t) => {
        const isOthers = t.name === "Others";
        const px = t.x * sx,
          py = t.y * sy,
          pw = t.w * sx,
          ph = t.h * sy;
        const baseSize = Math.max(9, Math.min(15, Math.sqrt(pw * ph) / 11));
        const fontSize = baseSize * fontScale;
        const showName = pw > 48 && ph > 22;
        const showVol = pw > 70 && ph > 50;
        const showVel = pw > 70 && ph > 70;
        const showSpark =
          !isOthers &&
          pw > 90 &&
          ph > 110 &&
          t.spark7d &&
          t.spark7d.length > 0;
        const showShare = totalRaw > 0 && pw > 110 && ph > 60;
        const sharePct = totalRaw > 0 ? (t.raw / totalRaw) * 100 : 0;
        const displayName = isOthers ? `Others +${foldedNames.length}` : t.name;
        const sparkW = Math.min(80, pw - 24);
        const sparkH = 16;
        const sparkStroke =
          t.velocity === null
            ? "rgba(180, 190, 185, 0.6)"
            : t.velocity >= 0
              ? COLORS.up
              : COLORS.down;
        if (!showName) return null;
        const tileStyle: CSSProperties = {
          position: "absolute",
          left: px,
          top: py,
          width: pw,
          height: ph,
          padding: "6px 8px",
          boxSizing: "border-box",
          pointerEvents: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          textAlign: "center",
          color: COLORS.text,
        };
        return (
          <div key={t.name} style={tileStyle}>
            {t.watched && (
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  right: 6,
                  color: COLORS.accent,
                  fontSize: 9,
                }}
              >
                ★
              </div>
            )}
            {showShare && (
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  left: 6,
                  color: "rgba(232,243,237,0.45)",
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  letterSpacing: 0.3,
                }}
              >
                {sharePct >= 10 ? sharePct.toFixed(0) : sharePct.toFixed(1)}%
              </div>
            )}
            <div
              style={{
                fontFamily: FONT_CONDENSED,
                fontWeight: 500,
                fontSize,
                lineHeight: 1.15,
                letterSpacing: "0.01em",
                maxWidth: "100%",
                overflow: "hidden",
                wordBreak: "break-word",
                color: isOthers ? COLORS.textDim : COLORS.text,
                fontStyle: isOthers ? "italic" : "normal",
              }}
            >
              {displayName}
            </div>
            {(showVol || showVel) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0,
                  marginTop: 2,
                }}
              >
                {showVol && (
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: Math.max(9, fontSize * 0.78),
                      color: "rgba(232,243,237,0.6)",
                      lineHeight: 1.2,
                    }}
                  >
                    {fmtRaw(t.raw)}
                  </div>
                )}
                {showVel && t.velocity !== null && (
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: Math.max(9, fontSize * 0.78),
                      color: t.velocity >= 0 ? COLORS.up : COLORS.down,
                      lineHeight: 1.2,
                    }}
                  >
                    {t.velocity >= 0 ? "+" : ""}
                    {t.velocity.toFixed(1)}%
                  </div>
                )}
                {showSpark && t.spark7d && (
                  <svg
                    width={sparkW}
                    height={sparkH}
                    style={{ marginTop: 4, opacity: 0.85 }}
                  >
                    <polyline
                      points={t.spark7d
                        .map((v, i) => {
                          const x =
                            (i / Math.max(1, t.spark7d!.length - 1)) *
                            (sparkW - 2) +
                            1;
                          const y = sparkH - v * (sparkH - 4) - 2;
                          return `${x.toFixed(1)},${y.toFixed(1)}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke={sparkStroke}
                      strokeWidth="1"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function heatColor(velocity: number | null): string {
  if (velocity === null) return "rgba(120, 130, 125, 0.18)";
  const intensity = Math.min(1, Math.abs(velocity) / 60);
  if (velocity >= 0) return `rgba(64, 220, 140, ${0.16 + intensity * 0.5})`;
  return `rgba(240, 80, 80, ${0.16 + intensity * 0.5})`;
}

// Tiles below this rendered pixel area are unreadable (can't fit name +
// volume) and get folded into a synthetic "Others" tile. Iterative: fold
// the smallest, re-squarify, repeat until everything's above threshold.
const MIN_TILE_AREA_PX = 70 * 50;

function makeOthers(folded: TreemapItem[]): TreemapItem {
  const value = folded.reduce((s, t) => s + t.value, 0);
  const raw = folded.reduce((s, t) => s + t.raw, 0);
  let velSum = 0;
  let velWeight = 0;
  for (const t of folded) {
    if (t.velocity !== null && t.raw > 0) {
      velSum += t.velocity * t.raw;
      velWeight += t.raw;
    }
  }
  const velocity = velWeight > 0 ? velSum / velWeight : null;
  return { name: "Others", value, raw, velocity };
}

function squarifyWithFolding(
  input: TreemapItem[],
  W: number,
  H: number,
  sx: number,
  sy: number,
): { placed: PlacedItem[]; foldedNames: string[] } {
  let working = input
    .filter((t) => t.value > 0)
    .map((t) => ({ ...t }))
    .sort((a, b) => b.value - a.value);
  if (working.length === 0) return { placed: [], foldedNames: [] };

  const folded: TreemapItem[] = [];

  // Need at least 2 non-Others tiles + 1 Others to make folding worthwhile.
  while (working.length > 2) {
    const placed = squarify(working, W, H);
    let smallest: PlacedItem | null = null;
    for (const p of placed) {
      if (p.name === "Others") continue;
      const a = p.w * sx * p.h * sy;
      const aSmall = smallest ? smallest.w * sx * smallest.h * sy : Infinity;
      if (a < aSmall) smallest = p;
    }
    if (!smallest) break;
    if (smallest.w * sx * smallest.h * sy >= MIN_TILE_AREA_PX) break;

    const idx = working.findIndex((t) => t.name === smallest!.name);
    if (idx < 0) break;
    folded.push(working[idx]);
    working.splice(idx, 1);

    const others = makeOthers(folded);
    const oIdx = working.findIndex((t) => t.name === "Others");
    if (oIdx >= 0) working[oIdx] = others;
    else working.push(others);
    working.sort((a, b) => b.value - a.value);
  }

  // No win for a single folded item — same tile size as the original. Undo.
  if (folded.length === 1) {
    working = working.filter((t) => t.name !== "Others");
    working.push(folded[0]);
    working.sort((a, b) => b.value - a.value);
    return { placed: squarify(working, W, H), foldedNames: [] };
  }

  return {
    placed: squarify(working, W, H),
    foldedNames: folded.map((t) => t.name),
  };
}

function squarify(input: TreemapItem[], W: number, H: number): PlacedItem[] {
  // Filter out zero values — treemap can't represent them. They'd otherwise
  // produce zero-area tiles that confuse the layout.
  const items = input
    .filter((t) => t.value > 0)
    .map((t) => ({ ...t }));
  if (items.length === 0) return [];

  const placed: PlacedItem[] = [];
  let x = 0,
    y = 0,
    rowW = W,
    rowH = H;
  let i = 0;

  while (i < items.length) {
    const remaining = items.slice(i);
    const remTotal = remaining.reduce((s, t) => s + t.value, 0);
    let bestN = 1;
    let bestRatio = Infinity;
    for (let n = 1; n <= remaining.length; n++) {
      const slice = remaining.slice(0, n);
      const sumN = slice.reduce((s, t) => s + t.value, 0);
      const shortSide = Math.min(rowW, rowH);
      const longSide = Math.max(rowW, rowH);
      const stripLong = (sumN / remTotal) * longSide;
      const worst = Math.max(
        ...slice.map((t) => {
          const w = (t.value / sumN) * shortSide;
          return Math.max(w / stripLong, stripLong / w);
        }),
      );
      if (worst < bestRatio) {
        bestRatio = worst;
        bestN = n;
      } else break;
    }
    const row = remaining.slice(0, bestN);
    const sumRow = row.reduce((s, t) => s + t.value, 0);
    const longSide = Math.max(rowW, rowH);
    const shortSide = Math.min(rowW, rowH);
    const stripLong = (sumRow / remTotal) * longSide;
    const horizontal = rowW >= rowH;
    let cx = x;
    let cy = y;
    for (const t of row) {
      const w = horizontal ? stripLong : (t.value / sumRow) * shortSide;
      const h = horizontal ? (t.value / sumRow) * shortSide : stripLong;
      placed.push({ ...t, x: cx, y: cy, w, h });
      if (horizontal) cy += h;
      else cx += w;
    }
    if (horizontal) {
      x += stripLong;
      rowW -= stripLong;
    } else {
      y += stripLong;
      rowH -= stripLong;
    }
    i += bestN;
  }
  return placed;
}
